import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import lawfullingoIcon from '../lawfullingo.jpeg';

const ModernHeader = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate(); // Use navigate hook
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    logout();
    setIsMobileMenuOpen(false);
    navigate('/'); // Redirect to home on logout
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  }

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled
        ? 'bg-white/95 backdrop-blur-md shadow-lg border-b border-slate-200'
        : 'bg-white/90 backdrop-blur-sm border-b border-slate-100'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm group-hover:scale-105 transition-transform duration-200">
                <img src={lawfullingoIcon} className="rounded-md" fill="currentColor" viewBox="0 0 24 24" alt='Icon'/>
              </div>
              <span className="text-xl font-heading font-bold text-slate-900 group-hover:text-slate-700 transition-colors duration-200">
                Lawfullingo
              </span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            <Link to="/" className="text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors duration-200 px-3 py-2 rounded-lg hover:bg-slate-100">
              Home
            </Link>
            <Link to="/courses" className="text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors duration-200 px-3 py-2 rounded-lg hover:bg-slate-100">
              Courses
            </Link>
            <Link to="/mock-tests" className="text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors duration-200 px-3 py-2 rounded-lg hover:bg-slate-100">
              Mock Tests
            </Link>
            <Link to="/notes" className="text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors duration-200 px-3 py-2 rounded-lg hover:bg-slate-100">
              Notes
            </Link>
            {user && (
              <Link to="/my-learning" className="text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors duration-200 px-3 py-2 rounded-lg hover:bg-slate-100">
                My Learning
              </Link>
            )}
          </nav>

          {/* User Actions */}
          <div className="flex items-center space-x-3">
            {user ? (
              <div className="relative group">
                <button className="flex items-center space-x-3 p-2 rounded-lg hover:bg-slate-100 transition-colors duration-200">
                  <div className="w-8 h-8 bg-slate-900 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                    {(user.fullName?.charAt(0) || user.email?.charAt(0) || 'U').toUpperCase()}
                  </div>
                </button>

                {/* Dropdown Menu */}
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-slate-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                   <div className="p-4 border-b border-slate-200">
                      <div className="text-sm font-medium text-slate-900 truncate">{user.fullName || 'User'}</div>
                      <div className="text-xs text-slate-500 truncate">{user.email}</div>
                    </div>
                  <div className="py-2">
                    <Link to="/my-learning" className="flex items-center px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 transition-colors duration-200">
                      <span className="mr-3 text-lg">📚</span>
                      My Learning
                    </Link>
                    <div className="border-t border-slate-200 my-1"></div>
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors duration-200"
                    >
                      <span className="mr-3 text-lg">🚪</span>
                      Logout
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="hidden md:flex items-center space-x-2">
                <Link to="/login" className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-all duration-200">
                  Login
                </Link>
                <Link to="/register" className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-slate-900 rounded-lg hover:bg-slate-800 transition-all duration-200 shadow-sm">
                  Sign Up
                </Link>
              </div>
            )}

            {/* Mobile Menu Toggle */}
            <button
              className="md:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors duration-200"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              <svg className="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-slate-200 py-4">
            <div className="space-y-2">
              <Link to="/" onClick={closeMobileMenu} className="block px-4 py-2 text-base font-medium text-slate-700 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors duration-200">
                Home
              </Link>
              <Link to="/courses" onClick={closeMobileMenu} className="block px-4 py-2 text-base font-medium text-slate-700 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors duration-200">
                Courses
              </Link>
              <Link to="/mock-tests" onClick={closeMobileMenu} className="block px-4 py-2 text-base font-medium text-slate-700 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors duration-200">
                Mock Tests
              </Link>
              <Link to="/notes" onClick={closeMobileMenu} className="block px-4 py-2 text-base font-medium text-slate-700 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors duration-200">
                Notes
              </Link>
              {user && (
                 <Link to="/my-learning" onClick={closeMobileMenu} className="block px-4 py-2 text-base font-medium text-slate-700 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors duration-200">
                    My Learning
                 </Link>
              )}
               <div className="border-t border-slate-200 my-2"></div>
               {!user && (
                 <div className="px-2 pt-2 space-y-2">
                    <Link to="/login" onClick={closeMobileMenu} className="block w-full text-center px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-all duration-200">
                      Login
                    </Link>
                    <Link to="/register" onClick={closeMobileMenu} className="block w-full text-center px-4 py-2 text-sm font-medium text-white bg-slate-900 rounded-lg hover:bg-slate-800 transition-all duration-200">
                      Sign Up
                    </Link>
                 </div>
               )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default ModernHeader;